import type { DocumentRecord, PnidSource } from '@/types/catalog'
import { apiService } from '@/service/api'
import { batchQueryDocs } from '@/utils/batchQueryDocs'
import { documentLoadQueue } from '@/utils/documentLoadQueue'

/**
 * A plan for resolving the source P&ID/PFD document behind each board `graph`
 * document. Board graph docs carry `properties.pnid` pointing at the source
 * document whose name/type the card should display.
 *
 * FS-511: the previous implementation issued one `getDocument` per graph doc
 * inside an unbounded `Promise.all` — on a P&ID-heavy board that is ~1 request
 * per board document. This planner makes the resolution cheap and batchable by:
 *   1. de-duplicating shared `pnid` ids (many graph docs can point at one P&ID),
 *   2. skipping ids whose record is already loaded in the local store, and
 *   3. returning the graph→pnid mapping so batch results can be re-keyed back to
 *      the `Map<graphDoc.id, DocumentRecord>` shape consumers expect.
 */
/**
 * Chunk size for the board pnid hydration batch. Larger than the boardSlice
 * default now that the server honors `page_size` (FS-511, clamped to 500
 * server-side): a P&ID-heavy board's worth of unique sources resolves in a
 * handful of round trips instead of one per 20.
 */
export const PNID_HYDRATION_CHUNK_SIZE = 200

/**
 * Telemetry for one board pnid-hydration pass. FS-511: reported so we can
 * confirm the per-doc fanout is gone (`network_requests` should be a small
 * number, or 0 when sources arrive inline) and catch regressions.
 */
export type PnidHydrationStats = {
  /** On-board graph docs that reference a source. */
  graphDocs: number
  /** Sources resolved from server-provided inline data (0 when the flag is off). */
  resolvedInline: number
  /** Network requests issued (batch chunks + queue fallbacks). */
  networkRequests: number
  /** Graph docs that ended up with a resolved source. */
  resolved: number
  /** Graph docs left without a source (stale pnid). */
  unresolved: number
}

export type PnidResolutionPlan = {
  /** Unique pnid source ids that still need to be fetched from the server. */
  pnidIdsToFetch: string[]
  /** graphDoc.id -> its `properties.pnid` source id. */
  graphIdToPnidId: Map<string, string>
  /** graphDoc.id -> source record already present in the local store (no fetch). */
  preResolved: Map<string, PnidSource>
}

/**
 * Build a {@link PnidResolutionPlan} from the loaded documents and the set of
 * document ids that are actually on the board.
 *
 * Pure: no I/O, no React. Safe to unit test in isolation.
 */
export function planPnidResolution(
  documents: DocumentRecord[],
  boardDocIds: Set<string>
): PnidResolutionPlan {
  const documentsById = new Map(documents.map((doc) => [doc.id, doc]))

  const graphIdToPnidId = new Map<string, string>()
  const preResolved = new Map<string, PnidSource>()
  const toFetch = new Set<string>()

  for (const doc of documents) {
    if (doc.type !== 'graph') continue
    const pnidId = doc.properties.pnid
    if (!pnidId) continue
    if (!boardDocIds.has(doc.id)) continue

    graphIdToPnidId.set(doc.id, pnidId)

    // If the source record is already in the store, reuse it instead of
    // re-fetching. Consumers only read name/type, which the store copy carries.
    const local = documentsById.get(pnidId)
    if (local) {
      preResolved.set(doc.id, local)
    } else {
      toFetch.add(pnidId)
    }
  }

  return { pnidIdsToFetch: [...toFetch], graphIdToPnidId, preResolved }
}

/**
 * Reorder pnid ids so the sources behind the given priority graph docs (e.g.
 * the currently-visible canvas page) are fetched first — they land in the first
 * chunk and resolve fastest. The full set is still returned; only the ORDER
 * changes, so off-page consumers (the board-wide name map) never regress.
 *
 * Pure: safe to unit test in isolation.
 */
export function orderPnidIdsByPriority(
  pnidIdsToFetch: string[],
  graphIdToPnidId: Map<string, string>,
  priorityGraphIds: Set<string>
): string[] {
  if (priorityGraphIds.size === 0) return pnidIdsToFetch

  const priorityPnidIds = new Set<string>()
  for (const graphId of priorityGraphIds) {
    const pnidId = graphIdToPnidId.get(graphId)
    if (pnidId) priorityPnidIds.add(pnidId)
  }
  if (priorityPnidIds.size === 0) return pnidIdsToFetch

  const first: string[] = []
  const rest: string[] = []
  for (const id of pnidIdsToFetch) {
    if (priorityPnidIds.has(id)) first.push(id)
    else rest.push(id)
  }
  return [...first, ...rest]
}

/**
 * Re-key resolved source records back to the `Map<graphDoc.id, DocumentRecord>`
 * shape that the board renderer consumes, merging the locally pre-resolved
 * records with the freshly fetched ones. Graph docs whose source could not be
 * resolved (stale `pnid`) are simply absent — consumers fall back to the graph
 * row's own name/type, matching the prior behaviour.
 */
export function assemblePnidMap(
  plan: PnidResolutionPlan,
  fetched: DocumentRecord[]
): Map<string, PnidSource> {
  const fetchedById = new Map(fetched.map((doc) => [doc.id, doc]))
  const result = new Map<string, PnidSource>(plan.preResolved)

  for (const [graphId, pnidId] of plan.graphIdToPnidId) {
    if (result.has(graphId)) continue // already pre-resolved from the store
    const doc = fetchedById.get(pnidId)
    if (doc) result.set(graphId, doc)
  }

  return result
}

/**
 * Resolve every board graph doc's source P&ID/PFD record into a
 * `Map<graphDoc.id, DocumentRecord>`.
 *
 * FS-511: the unresolved sources are fetched through the batched
 * `/v1/document/query` endpoint ({@link batchQueryDocs}) — chunked and
 * parallelised — instead of one `getDocument` per graph doc. On a board where
 * almost every document is a P&ID-derived graph, this collapses ~1 request per
 * board document into ~one request per 20 unique sources, eliminating the
 * connection-saturating fanout that left the board stuck (`ERR_INSUFFICIENT_RESOURCES`).
 *
 * Sources the batch endpoint does not return are retried individually through
 * the concurrency-capped {@link documentLoadQueue} (see
 * {@link resolveMissingViaQueue}): the single-doc `getDocument` path resolves
 * forwarded/soft-deleted (ENG-2414) and sample-tenant sources that the batch
 * `IN (...)` query does not. Anything still unresolved (a genuinely stale
 * `pnid`) is absent from the map and the consumer falls back to the graph row's
 * own name/type.
 */
export async function resolvePnidDocuments(
  documents: DocumentRecord[],
  boardDocIds: Set<string>,
  options?: {
    signal?: AbortSignal
    onStats?: (stats: PnidHydrationStats) => void
    /**
     * Graph-doc ids to resolve first (e.g. the visible page). Only affects fetch
     * ORDER — the whole board is still resolved, so off-page names never regress.
     */
    priorityIds?: Set<string>
  }
): Promise<Map<string, PnidSource>> {
  // FS-511 PR11 inline fast-path: when the board documents were loaded with
  // `include_pnid_source`, every on-board graph doc carries `pnid_source` (a
  // resolved record, or null for a stale source). The server already did the
  // graph->source join, so resolve from it with ZERO network requests.
  const onBoardGraphDocs = documents.filter(
    (doc) => doc.type === 'graph' && doc.properties.pnid && boardDocIds.has(doc.id)
  )
  // All-or-nothing by design: a single board load resolves `documents` in one
  // atomic set (boardSlice.getDocuments), so either every doc carries inline
  // pnid_source (flag on + server resolved) or none do (flag off / older
  // payload). We therefore only take the fast-path when ALL on-board graph docs
  // are inline; a partial set (a transient mixed state) safely falls through to
  // the batch path below rather than half-resolving from stale inline data.
  const everySourceInline =
    onBoardGraphDocs.length > 0 &&
    onBoardGraphDocs.every((doc) => doc.pnid_source !== undefined)
  if (everySourceInline) {
    const inlineMap = new Map<string, PnidSource>()
    for (const doc of onBoardGraphDocs) {
      if (doc.pnid_source) inlineMap.set(doc.id, doc.pnid_source)
    }
    options?.onStats?.({
      graphDocs: onBoardGraphDocs.length,
      resolvedInline: inlineMap.size,
      networkRequests: 0,
      resolved: inlineMap.size,
      unresolved: onBoardGraphDocs.length - inlineMap.size,
    })
    return inlineMap
  }

  const plan = planPnidResolution(documents, boardDocIds)
  const graphDocs = plan.graphIdToPnidId.size
  let networkRequests = 0

  const fetchIds = options?.priorityIds
    ? orderPnidIdsByPriority(plan.pnidIdsToFetch, plan.graphIdToPnidId, options.priorityIds)
    : plan.pnidIdsToFetch

  let fetched: DocumentRecord[] = []
  if (fetchIds.length > 0) {
    networkRequests += Math.ceil(fetchIds.length / PNID_HYDRATION_CHUNK_SIZE)
    fetched = await batchQueryDocs(fetchIds, {
      signal: options?.signal,
      chunkSize: PNID_HYDRATION_CHUNK_SIZE,
    })
  }
  const map = assemblePnidMap(plan, fetched)

  // Graph docs the batch could not resolve — retry their sources individually
  // through the bounded queue. Dedup the pnid ids so a shared source is
  // retried once.
  const unresolved = [...plan.graphIdToPnidId].filter(([graphId]) => !map.has(graphId))
  if (unresolved.length > 0) {
    const missingPnidIds = [...new Set(unresolved.map(([, pnidId]) => pnidId))]
    networkRequests += missingPnidIds.length
    const fallback = await resolveMissingViaQueue(missingPnidIds, {
      signal: options?.signal,
    })
    for (const [graphId, pnidId] of unresolved) {
      const doc = fallback.get(pnidId)
      if (doc) map.set(graphId, doc)
    }
  }

  options?.onStats?.({
    graphDocs,
    resolvedInline: 0,
    networkRequests,
    resolved: map.size,
    unresolved: graphDocs - map.size,
  })
  return map
}

/**
 * Resolve `pnidIds` one at a time through the concurrency-capped
 * {@link documentLoadQueue}, returning a `Map<requestedPnidId, DocumentRecord>`.
 *
 * Keyed by the *requested* id (not the returned doc's id) so a forwarded source
 * — where `getDocument` follows a soft-deleted row to its winning replacement
 * with a different id — still re-keys correctly back onto the graph doc.
 * Concurrency is bounded by the queue (so even a board full of stale refs can
 * never reproduce the FS-511 fanout); genuinely missing sources resolve to
 * `null` and are omitted.
 */
async function resolveMissingViaQueue(
  pnidIds: string[],
  options?: { signal?: AbortSignal }
): Promise<Map<string, PnidSource>> {
  const entries = await Promise.all(
    pnidIds.map(async (id): Promise<[string, PnidSource] | null> => {
      try {
        const doc = await documentLoadQueue.add(`pnid-fallback:${id}`, () =>
          apiService.getDocument(id, { signal: options?.signal })
        )
        return [id, doc]
      } catch (error) {
        // Propagate aborts (board switch); swallow not-found/other errors so a
        // stale ref just drops out.
        if ((error as Error).name === 'AbortError') throw error
        return null
      }
    })
  )

  return new Map(
    entries.filter((entry): entry is [string, PnidSource] => entry !== null)
  )
}
